export type OrgType =
  | 'manufacturer'
  | 'retailer'
  | 'distributor'
  | 'vendor'
  | 'internal'
  | 'all';

// Organization type enum for database operations
export enum OrganizationType {
  MANUFACTURER = 'manufacturer',
  RETAILER = 'retailer',
  DISTRIBUTOR = 'distributor',
  VENDOR = 'vendor',
  INTERNAL = 'internal'
}

// Helper function to get organization type display name
export function getOrganizationTypeDisplayName(type: OrgType | null): string {
  if (!type || type === 'all') return 'Unknown';

  switch (type) {
    case OrganizationType.MANUFACTURER:
      return 'Manufacturer';
    case OrganizationType.RETAILER:
      return 'Retailer';
    case OrganizationType.DISTRIBUTOR:
      return 'Distributor';
    case OrganizationType.VENDOR:
      return 'Vendor';
    case OrganizationType.INTERNAL:
      return 'Internal';
    default:
      return 'Unknown';
  }
}

// Helper function to validate organization type
export function isValidOrganizationType(type: string): type is OrgType {
  return [
    OrganizationType.MANUFACTURER,
    OrganizationType.RETAILER,
    OrganizationType.DISTRIBUTOR,
    OrganizationType.VENDOR,
    OrganizationType.INTERNAL,
    'all'
  ].includes(type as OrgType);
}

// Minimal menu item and group shapes used by the navigation system
import type { ElementType } from 'react';
import { Logger } from '../logger';

export interface MenuItem {
  id: string;
  label: string;
  path: string;
  // icon is a string name that maps to a React component
  icon?: string;
  // which org types can see this item; 'all' means visible to everyone
  availableFor?: OrgType[] | 'all';
  // if true, the navigation system should open this link in a new browser window/tab
  openInNewWindow?: boolean;
  // Module keys that must be enabled for this item to be visible
  requiredModules?: string[];
  // Feature flags that must be enabled for this item to be visible
  featureFlags?: string[];
}

export interface MenuGroup {
  group: string;
  items: MenuItem[];
}

// Get organization type from session instead of using default
export async function getCurrentOrgType(): Promise<OrgType> {
  const requestId = Math.random().toString(36).substring(2, 12);

  try {
    const { getSessionData } = await import('@/lib/sessionUtils');
    const session = await getSessionData();

    Logger.info('OrgTypes', 'Retrieved session data for org type', {
      orgId: session.orgId,
      userId: session.userId,
      orgType: session.orgType,
      hasError: !!session.error,
      isDatabaseError: session.isDatabaseError,
      requestId
    });

    const finalOrgType = (session.orgType as OrgType) || DEFAULT_ORG_TYPE;

    Logger.info('OrgTypes', 'Final org type determination', {
      sessionOrgType: session.orgType,
      defaultOrgType: DEFAULT_ORG_TYPE,
      finalOrgType,
      usedDefault: !session.orgType,
      requestId
    });

    return finalOrgType;
  } catch (error) {
    Logger.apiError('OrgTypes', 'Failed to get org type from session, using default', error, {
      method: 'GET',
      action: 'getCurrentOrgType',
      requestId,
      userId: undefined,
      error: error instanceof Error ? error.stack : String(error)
    });

    Logger.info('OrgTypes', 'Using default org type due to error', {
      defaultOrgType: DEFAULT_ORG_TYPE,
      requestId
    });

    return DEFAULT_ORG_TYPE;
  }
}

// Temporary default org type used during development until session wiring is added
export const DEFAULT_ORG_TYPE: OrgType = 'manufacturer';
